import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, FlatList } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ModalBottomSheet } from './ModalBottomSheet';
import { getDistrictsForProvince, provinceNames } from '../utils/turkeyData';

type Theme = 'rose' | 'orange';

const THEME = {
  rose: {
    border: 'border-zinc-200 dark:border-zinc-800',
    bg: 'bg-white dark:bg-zinc-900',
    bgMuted: 'bg-zinc-50 dark:bg-zinc-950',
    text: 'text-zinc-900 dark:text-zinc-100',
    label: 'text-zinc-900 dark:text-zinc-100',
    activeBorder: 'border-zinc-900 dark:border-zinc-100',
    activeBg: 'bg-zinc-100 dark:bg-zinc-800',
    modalBg: 'bg-white dark:bg-zinc-900',
    itemActive: 'bg-zinc-100 dark:bg-zinc-800',
  },
  orange: {
    border: 'border-zinc-200 dark:border-zinc-800',
    bg: 'bg-white dark:bg-zinc-900',
    bgMuted: 'bg-zinc-50 dark:bg-zinc-950',
    text: 'text-zinc-900 dark:text-zinc-100',
    label: 'text-zinc-900 dark:text-zinc-100',
    activeBorder: 'border-zinc-900 dark:border-zinc-100',
    activeBg: 'bg-zinc-100 dark:bg-zinc-800',
    modalBg: 'bg-white dark:bg-zinc-900',
    itemActive: 'bg-zinc-100 dark:bg-zinc-800',
  },
} as const;

type Props = {
  city: string;
  district: string;
  onCityChange: (name: string) => void;
  onDistrictChange: (name: string) => void;
  theme?: Theme;
};

export function CityDistrictSelect({
  city,
  district,
  onCityChange,
  onDistrictChange,
  theme = 'rose',
}: Props) {
  const insets = useSafeAreaInsets();
  const t = THEME[theme];
  const [cityModal, setCityModal] = useState(false);
  const [districtModal, setDistrictModal] = useState(false);

  const cities = useMemo(() => provinceNames(), []);
  const districts = useMemo(() => getDistrictsForProvince(city), [city]);

  function onPickCity(name: string) {
    onCityChange(name);
    const nextDistricts = getDistrictsForProvince(name);
    if (!nextDistricts.includes(district)) {
      onDistrictChange('');
    }
    setCityModal(false);
  }

  return (
    <View>
      <Text className={`mb-2 font-medium ${t.label}`}>İl</Text>
      <TouchableOpacity
        activeOpacity={0.88}
        onPress={() => setCityModal(true)}
        className={`mb-4 rounded-xl border ${t.border} ${t.bg} px-4 py-3`}>
        <Text className={city ? `text-base ${t.text}` : 'text-base text-zinc-400'}>
          {city || 'İl seçin'}
        </Text>
      </TouchableOpacity>

      <Text className={`mb-2 font-medium ${t.label}`}>İlçe (isteğe bağlı)</Text>
      <TouchableOpacity
        activeOpacity={0.88}
        disabled={!city}
        onPress={() => city && setDistrictModal(true)}
        className={`mb-8 rounded-xl border ${t.border} ${!city ? 'opacity-50' : ''} ${t.bg} px-4 py-3`}>
        <Text className={district ? `text-base ${t.text}` : 'text-base text-zinc-400'}>
          {!city ? 'Önce il seçin' : district || 'İl geneli — ilçe seçmeden devam'}
        </Text>
      </TouchableOpacity>

      <ModalBottomSheet
        visible={cityModal}
        onRequestClose={() => setCityModal(false)}
        keyboardAvoiding={false}>
        <View
          className={`max-h-[70%] rounded-t-3xl border ${t.border} ${t.modalBg} px-5 pt-4`}
          style={{ paddingBottom: 24 + insets.bottom }}>
          <View className="mb-3 h-1 w-10 self-center rounded-full bg-zinc-200 dark:bg-zinc-700" />
          <Text className="mb-3 text-lg font-bold text-zinc-900 dark:text-zinc-100">İl seçin</Text>
          <FlatList
            data={cities}
            keyExtractor={item => item}
            keyboardShouldPersistTaps="handled"
            renderItem={({ item }) => (
              <TouchableOpacity
                onPress={() => onPickCity(item)}
                className={`mb-1 rounded-2xl border px-4 py-3.5 ${
                  city === item ? `${t.activeBorder} ${t.itemActive}` : `${t.border} ${t.bgMuted}`
                }`}>
                <Text className="text-base text-zinc-900 dark:text-zinc-100">{item}</Text>
              </TouchableOpacity>
            )}
          />
        </View>
      </ModalBottomSheet>

      <ModalBottomSheet
        visible={districtModal}
        onRequestClose={() => setDistrictModal(false)}
        keyboardAvoiding={false}>
        <View
          className={`max-h-[70%] rounded-t-3xl border ${t.border} ${t.modalBg} px-5 pt-4`}
          style={{ paddingBottom: 24 + insets.bottom }}>
          <View className="mb-3 h-1 w-10 self-center rounded-full bg-zinc-200 dark:bg-zinc-700" />
          <Text className="mb-3 text-lg font-bold text-zinc-900 dark:text-zinc-100">İlçe seçin</Text>
          <TouchableOpacity
            onPress={() => {
              onDistrictChange('');
              setDistrictModal(false);
            }}
            className={`mb-2 rounded-2xl border px-4 py-3.5 ${t.border} ${t.bgMuted}`}>
            <Text className="text-center text-base text-zinc-600 dark:text-zinc-300">
              İl geneli (ilçe yok)
            </Text>
          </TouchableOpacity>
          <FlatList
            data={districts}
            keyExtractor={item => item}
            keyboardShouldPersistTaps="handled"
            renderItem={({ item }) => (
              <TouchableOpacity
                onPress={() => {
                  onDistrictChange(item);
                  setDistrictModal(false);
                }}
                className={`mb-1 rounded-2xl border px-4 py-3.5 ${
                  district === item ? `${t.activeBorder} ${t.itemActive}` : `${t.border} ${t.bgMuted}`
                }`}>
                <Text className="text-base text-zinc-900 dark:text-zinc-100">{item}</Text>
              </TouchableOpacity>
            )}
          />
        </View>
      </ModalBottomSheet>
    </View>
  );
}
